import { applyOperations, parseAdvancedConfig } from './operationsProcessor';
import jexl from 'jexl';

/**
 * Extracts all template expressions from a pattern string
 * @param pattern - The pattern string containing template expressions like {{entry.field}}
 * @returns Array of expression strings without the {{}} wrapper
 */
function getExpressionsFromPattern(pattern: string): string[] {
    if (!pattern || typeof pattern !== 'string') {
        return [];
    }
    const regex = /{{([^}]+)}}/g;
    return [...pattern.matchAll(regex)].map(match => match[1]);
}
  
interface IncludeOnlyResult {
    include: string[] | null;
    only: Record<string, string[]> | null;
}

/**
 * Represents a warning about a missing or failed template expression
 */
interface ResolutionWarning {
    expression: string;  // The expression that failed (e.g., "entry.url")
    reason: 'missing_value' | 'evaluation_error';
    placeholder: string; // The original {{expression}} in the template
}

/**
 * Reason types explained:
 * - 'missing_value': The field exists in the entry but has no value (null/undefined/empty string)
 * - 'evaluation_error': The expression itself is invalid or the field path doesn't exist
 */

/**
 * Result of URL pattern resolution including warnings
 */
interface ResolutionResult {
    url: string;
    warnings: ResolutionWarning[];
    hasMissingValues: boolean;
}

/**
 * Helper function to extract field UID from array notation or bracket notation
 * @param fieldUid - Field UID that may contain array notation like "field[0]" or bracket notation like "field[environment][locale]"
 * @returns Clean field UID without array/bracket notation (extracts the base field name before the first '[' bracket)
 */
function extractFieldUid(fieldUid: string): string {
    // Handle both numeric array indices (field[0]) and bracket notation (field[environment][locale])
    // Extract the field name before the first '[' bracket
    const bracketIndex = fieldUid.indexOf('[');
    return bracketIndex >= 0 ? fieldUid.substring(0, bracketIndex) : fieldUid;
}

/**
 * Helper function to clean path segments by removing array notation
 * @param path - Array of path segments
 * @returns Cleaned path segments without array notation
 */
function cleanPathSegments(path: string[]): string[] {
    return path.map(segment => extractFieldUid(segment));
}

/**
 * Helper function to check if there are more references in the remaining path
 * @param path - Full path array
 * @param startIndex - Index to start checking from
 * @param currentSchema - Current schema to traverse
 * @param getContentType - Function to get content type by UID
 * @param getField - Function to get field by UID
 * @returns Object containing hasMoreReferences flag and nextReferenceIndex
 */
function checkForMoreReferences(
    path: string[],
    startIndex: number,
    currentSchema: any[],
    getContentType: (uid: string) => any,
    getField: (fields: any[], uid: string) => any
): { hasMoreReferences: boolean; nextReferenceIndex: number } {
    let hasMoreReferences = false;
    let nextReferenceIndex = -1;
    let tempSchema = currentSchema;
    
    for (let j = startIndex; j < path.length - 1; j++) {
        const futureFieldUid = extractFieldUid(path[j]);
        const futureField = getField(tempSchema, futureFieldUid);
        
        if (futureField) {
            if (futureField.data_type === 'reference') {
                hasMoreReferences = true;
                nextReferenceIndex = j;
                break;
            } else if (futureField.data_type === 'group') {
                tempSchema = futureField.schema || [];
            } else if (futureField.data_type === 'global_field') {
                tempSchema = futureField.schema || [];
            } else if (futureField.data_type === 'blocks') {
                const blockUid = path[j + 1];
                if (blockUid) {
                    const block = futureField.blocks?.find((b: any) => b.uid === extractFieldUid(blockUid));
                    if (block) {
                        tempSchema = block.schema || [];
                        j++; // Skip the block UID
                    }
                }
            }
        }
    }
    
    return { hasMoreReferences, nextReferenceIndex };
}

/**
 * Helper function to process reference fields
 * @param field - The reference field
 * @param path - Full path array
 * @param i - Current index in path
 * @param includePath - Current include path
 * @param include - Set to add include paths
 * @param only - Object to add only paths
 * @param referenceIndices - Array tracking reference indices
 * @param getContentType - Function to get content type by UID
 * @param getField - Function to get field by UID
 * @returns Object containing updated state
 */
function processReferenceField(
    field: any,
    path: string[],
    i: number,
    includePath: string[],
    include: Set<string>,
    only: Record<string, Set<string>>,
    referenceIndices: number[],
    getContentType: (uid: string) => any,
    getField: (fields: any[], uid: string) => any,
    rootFields?: Set<string>,
    hasNestedFields?: boolean,
    isLastReference?: boolean
): { currentContentType: any; currentSchema: any[] } {
    const fullIncludePath = includePath.join('.');
    include.add(fullIncludePath);
    
    const referencedTypeUid = field.reference_to?.[0];
    let currentContentType = null;
    let currentSchema: any[] = [];
    
    if (referencedTypeUid) {
        const referencedContentType = getContentType(referencedTypeUid);
        if (referencedContentType) {
            currentContentType = referencedContentType;
            currentSchema = referencedContentType.schema;
            
            // Handle only path logic using helper function
            handleOnlyPathLogic(
                path, i, currentSchema, fullIncludePath, only, referenceIndices, getContentType, getField
            );
        }
    }
    
    referenceIndices.push(i);
    
    // Handle nested fields with references - only process this logic once on the last reference
    if (rootFields && hasNestedFields && isLastReference && referenceIndices.length > 0) {
        const firstRefIndex = referenceIndices[0];
        const refPath = cleanPathSegments(path.slice(0, firstRefIndex + 1)).join('.');
        rootFields.add(refPath);
        
        // Only remove root field if the first reference is not at index 0
        if (firstRefIndex > 0) {
            const rootFieldUid = extractFieldUid(path[0]);
            rootFields.delete(rootFieldUid);
        }
    }
    
    return { currentContentType, currentSchema };
}

/**
 * Helper function to handle only path logic for reference fields
 * @param path - Full path array
 * @param i - Current index in path
 * @param currentSchema - Current schema
 * @param fullIncludePath - Full include path string
 * @param only - Object to add only paths
 * @param referenceIndices - Array tracking reference indices
 * @param getContentType - Function to get content type by UID
 * @param getField - Function to get field by UID
 * @returns void
 */
function handleOnlyPathLogic(
    path: string[],
    i: number,
    currentSchema: any[],
    fullIncludePath: string,
    only: Record<string, Set<string>>,
    referenceIndices: number[],
    getContentType: (uid: string) => any,
    getField: (fields: any[], uid: string) => any
): void {
    // Check for more references in the remaining path
    const { hasMoreReferences, nextReferenceIndex } = checkForMoreReferences(
        path, i + 1, currentSchema, getContentType, getField
    );
    
    if (hasMoreReferences && nextReferenceIndex > 0) {
        // Add path to next reference
        const pathToNextRef = cleanPathSegments(path.slice(i + 1, nextReferenceIndex + 1)).join('.');
        if (!only[fullIncludePath]) only[fullIncludePath] = new Set();
        only[fullIncludePath].add(pathToNextRef);
    } else {
        // Add complete remaining path
        const remainingPath = cleanPathSegments(path.slice(i + 1)).join('.');
        
        if (referenceIndices.length > 0) {
            // Handle nested references
            const firstRefIndex = referenceIndices[0];
            const pathFromFirstRef = path.slice(firstRefIndex + 1, i + 1);
            
            if (pathFromFirstRef.length > 0) {
                const nestedRefPath = cleanPathSegments(pathFromFirstRef).join('.');
                if (!only[nestedRefPath]) only[nestedRefPath] = new Set();
                only[nestedRefPath].add(remainingPath);
            } else {
                if (!only[fullIncludePath]) only[fullIncludePath] = new Set();
                only[fullIncludePath].add(remainingPath);
            }
        } else {
            if (!only[fullIncludePath]) only[fullIncludePath] = new Set();
            only[fullIncludePath].add(remainingPath);
        }
    }
}

/**
 * Helper function to process different field types
 * @param field - The field to process
 * @param path - Full path array
 * @param i - Current index in path
 * @param includePath - Current include path
 * @param include - Set to add include paths
 * @param only - Object to add only paths
 * @param referenceIndices - Array tracking reference indices
 * @param getContentType - Function to get content type by UID
 * @param getField - Function to get field by UID
 * @param rootFields - Set to add root fields
 * @param hasNestedFields - Flag indicating if there are nested fields
 * @returns Object containing updated state
 */
function processFieldType(
    field: any,
    path: string[],
    i: number,
    includePath: string[],
    include: Set<string>,
    only: Record<string, Set<string>>,
    referenceIndices: number[],
    getContentType: (uid: string) => any,
    getField: (fields: any[], uid: string) => any,
    rootFields: Set<string>,
    hasNestedFields: boolean
): { currentContentType: any; currentSchema: any[]; skipNext: boolean; hasNestedFields: boolean } {
    let currentContentType = null;
    let currentSchema: any[] = [];
    let skipNext = false;
    let updatedHasNestedFields = hasNestedFields;

    switch (field.data_type) {
        case 'reference': {
            // Check if this is the last reference field in the path
            const { hasMoreReferences } = checkForMoreReferences(
                path, i + 1, currentSchema, getContentType, getField
            );
            const isLastReference = !hasMoreReferences;
            
            const result = processReferenceField(
                field, path, i, includePath, include, only, referenceIndices, getContentType, getField, rootFields, hasNestedFields, isLastReference
            );
            currentContentType = result.currentContentType;
            currentSchema = result.currentSchema;
            break;
        }
        case 'group': {
            currentSchema = field.schema || [];
            updatedHasNestedFields = true;
            break;
        }
        case 'blocks': {
            const result = processModularBlocks(field, path, i, includePath);
            currentSchema = result.currentSchema;
            skipNext = result.skipNext;
            updatedHasNestedFields = true;
            break;
        }
        case 'global_field': {
            currentSchema = field.schema || [];
            updatedHasNestedFields = true;
            break;
        }
    }

    return { currentContentType, currentSchema, skipNext, hasNestedFields: updatedHasNestedFields };
}

/**
 * Helper function to process modular blocks
 * @param field - The blocks field
 * @param path - Full path array
 * @param i - Current index in path
 * @param includePath - Current include path
 * @returns Object containing updated state
 */
function processModularBlocks(
    field: any,
    path: string[],
    i: number,
    includePath: string[]
): { currentSchema: any[]; skipNext: boolean } {
    const nextFieldUid = path[i + 1];
    let currentSchema: any[] = [];
    let skipNext = false;
    
    if (nextFieldUid) {
        const block = field.blocks?.find((b: any) => b.uid === nextFieldUid);
        if (block) {
            includePath.push(nextFieldUid);
            currentSchema = block.schema || [];
            skipNext = true; // Skip the next iteration since we've processed the block UID
        }
    }
    
    return { currentSchema, skipNext };
}

/**
 * Helper function to process entry expressions
 * @param expr - The entry expression
 * @param contentTypes - Array of content type definitions
 * @param rootTypeUid - The root content type UID
 * @param include - Set to add include paths
 * @param only - Object to add only paths
 * @param rootFields - Set to add root fields
 * @returns void
 */
function processEntryExpression(
    expr: string,
    contentTypes: any[],
    rootTypeUid: string,
    include: Set<string>,
    only: Record<string, Set<string>>,
    rootFields: Set<string>
): void {
    const path = expr.replace(/^entry\./, '').split('.');
    const getContentType = (uid: string) => contentTypes.find((ct) => ct.uid === uid);
    const getField = (fields: any[], uid: string) => fields.find((f) => f.uid === uid);
    
    let currentContentType = getContentType(rootTypeUid);
    if (!currentContentType) return;
    
    let currentSchema = currentContentType.schema;
    const includePath: string[] = [];
    let hasNestedFields = false;
    const referenceIndices: number[] = [];
    
    for (let i = 0; i < path.length; i++) {
        let fieldUid = extractFieldUid(path[i]);
        const field = getField(currentSchema, fieldUid);
        
        if (!field) break;
        
        // Track root-level fields
        if (i === 0) {
            rootFields.add(fieldUid);
        }
        
        // Process non-terminal fields
        if (i < path.length - 1) {
            includePath.push(fieldUid);
            
            const result = processFieldType(
                field, path, i, includePath, include, only, referenceIndices, 
                getContentType, getField, rootFields, hasNestedFields
            );
            
            currentContentType = result.currentContentType;
            currentSchema = result.currentSchema;
            hasNestedFields = result.hasNestedFields;
            
            if (result.skipNext) i++; // Skip next iteration for modular blocks
        }
    }
    
    // Handle nested fields at root level
    if (hasNestedFields && referenceIndices.length === 0) {
        const cleanedPath = cleanPathSegments(path).join('.');
        rootFields.add(cleanedPath);
        const rootFieldUid = extractFieldUid(path[0]);
        rootFields.delete(rootFieldUid);
    }
}

/**
 * Helper function to convert internal data structures to API parameters
 * @param include - Set of include paths
 * @param only - Object of only paths
 * @param rootFields - Set of root fields
 * @returns Formatted API parameters
 */
function formatApiParameters(
    include: Set<string>,
    only: Record<string, Set<string>>,
    rootFields: Set<string>
): IncludeOnlyResult {
    // Add root fields to BASE if any exist
    if (rootFields.size > 0) {
        only['BASE'] = rootFields;
    }

    // Convert sets to arrays and handle empty cases
    let includeParam: string[] | null = Array.from(include);
    let onlyParam: Record<string, string[]> | null = Object.fromEntries(
        Object.entries(only).map(([k, v]) => [k, Array.from(v)])
    );
    
    if (includeParam.length === 0) {
        includeParam = null;
    }
    if (Object.keys(onlyParam).length === 0) {
        onlyParam = null;
    }
    
    return {
        include: includeParam,
        only: onlyParam,
    };
}

/**
 * Generates include and only parameters for API calls based on URL pattern
 * @param pattern - The URL pattern containing template expressions
 * @param contentTypes - Array of content type definitions
 * @param rootTypeUid - The root content type UID
 * @returns Object containing include and only parameters for API optimization
 */
function generateIncludeOnlyFromPattern(
    pattern: string,
    contentTypes: any[],
    rootTypeUid: string
): IncludeOnlyResult {
    // Handle null/undefined inputs gracefully
    if (!pattern || !contentTypes || !rootTypeUid) {
        return { include: null, only: null };
    }

    try {
        const expressions = getExpressionsFromPattern(pattern);
        const include = new Set<string>();
        const only: Record<string, Set<string>> = {};
        const rootFields = new Set<string>();

        // Check if root content type exists once
        const hasValidRootType = contentTypes.some((ct) => ct.uid === rootTypeUid);
        
        for (const expr of expressions) {
            if (expr.startsWith('entry.')) {
                processEntryExpression(expr, contentTypes, rootTypeUid, include, only, rootFields);
            } else if (expr.startsWith('taxonomy:')) {
                rootFields.add('taxonomies');
            }
        }
        
        // Add 'url' only once if we have a valid root content type
        if (hasValidRootType) {
            rootFields.add('url');
        }
    
        return formatApiParameters(include, only, rootFields);
    } catch (error) {
        console.error('Error generating include and only parameters:', error);
        return { include: null, only: null };
    }
}


function buildQueryString({ include, only }: { include: string[] | null, only: Record<string, string[]> | null }): string {
    const params: string[] = [];
  
    // Add include[]
    for (const inc of include || []) {
      params.push(`include[]=${encodeURIComponent(inc)}`);
    }
  
    // Add only[field][]
    for (const [field, values] of Object.entries(only || {})) {
      for (const val of values) {
        params.push(`only[${encodeURIComponent(field)}][]=${encodeURIComponent(val)}`);
       }
    }
  
    return params.join('&');
}

function buildAxiosParams({ include, only }: { include: string[] | null, only: Record<string, string[]> | null }): Record<string, any> {
    const params: Record<string, any> = {};
  
    // Add include as array - axios will convert to include[]=val1&include[]=val2
    if (include && include.length > 0) {
      params['include'] = include;
    }
  
    // Add only fields with explicit bracket notation for Contentstack API
    for (const [field, values] of Object.entries(only || {})) {
      if (values.length > 0) {
        // Use the format only[field][] = value to ensure proper serialization
        params[`only[${field}]`] = values;
      }
    }
  
    return params;
}



/**
 * Evaluates a template expression securely
 * This replaces the previous unsafe Function constructor approach
 */
function evaluateExpression(
  expression: string,
  context: Record<string, any>
): { value: any; success: boolean } {
  // Handle taxonomy lookup (special case that doesn't need expression evaluation)
  if (expression.startsWith('taxonomy:')) {
    const taxonomy_uid = expression.split(':')[1];
    
    // Extract ALL terms for this taxonomy (not just first)
    const allTerms = context?.entry?.taxonomies
      ?.filter((t: any) => t.taxonomy_uid === taxonomy_uid)
      ?.map((t: any) => t.term_uid) || [];
    
    if (allTerms.length === 0) {
      return { value: undefined, success: false };
    }
    
    // Return array of all terms (already sorted by hierarchy in UI-React)
    return { value: allTerms, success: true };
  }
  
  // Use jexl for secure expression evaluation - handles both simple and complex expressions
  try {
    const value = jexl.evalSync(expression, context);
    return { value, success: value !== undefined && value !== null };
  } catch (error) {
    console.warn('Expression evaluation failed:', expression, error);
    return { value: undefined, success: false };
  }
}

/**
 * Checks if a value should be considered "missing"
 */
function isMissingValue(value: any): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Renders a template string by replacing {{expressions}} with actual values
 */
function renderTemplate(
  template: string, 
  context: Record<string, any>,
  advancedConfig?: any,
  warnings?: ResolutionWarning[]
): string {
    return template.replace(/{{(.*?)}}/g, (match, expression) => {
      // Evaluate the expression
      const { value: rawValue, success: evaluationSuccess } = evaluateExpression(expression, context);
      
      // Apply operations if advanced config is provided
      if (advancedConfig?.fields?.[expression]?.operations) {
        const processedValue = applyOperations(rawValue, advancedConfig.fields[expression].operations, context);
        
        // Check if value is still missing after operations
        if (isMissingValue(processedValue)) {
          warnings?.push({
            expression: expression,
            reason: 'missing_value',
            placeholder: match
          });
          return match; // Keep placeholder
        }
        return processedValue;
      }
      
      // Check if value is missing or evaluation failed
      if (!evaluationSuccess || isMissingValue(rawValue)) {
        warnings?.push({
          expression: expression,
          reason: evaluationSuccess ? 'missing_value' : 'evaluation_error',
          placeholder: match
        });
        return match; // Keep placeholder
      }
      
      // Return the resolved value
      return typeof rawValue === 'string' ? rawValue : String(rawValue);
    });
}
  

/**
 * Resolves URL patterns by replacing template expressions with actual values
 * @param hostPattern - The host pattern template (e.g., "https://{{environment}}.example.com")
 * @param urlPattern - The URL path pattern template (e.g., "/{{entry.slug}}")
 * @param entry - The entry data to use for template resolution
 * @param additionalContext - Additional context variables for template resolution
 * @param advancedConfig - JSON string or object containing operations configuration
 * @returns ResolutionResult containing the resolved URL and any warnings
 */
function resolveUrlPattern(
    hostPattern: string | undefined, 
    urlPattern: string | undefined, 
    entry: any, 
    additionalContext: Record<string, any> = {},
    advancedConfig?: string | any
): ResolutionResult {
    try {
        // Parse advanced config if it's a JSON string
        let parsedAdvancedConfig: any = null;
        if (advancedConfig) {
          if (typeof advancedConfig === 'string') {
            parsedAdvancedConfig = parseAdvancedConfig(advancedConfig);
          } else {
            parsedAdvancedConfig = advancedConfig;
          }
        }
        
        const context = { entry, ...additionalContext };
        const warnings: ResolutionWarning[] = [];
        
        const hostPatternResolvedTemplate = hostPattern ? renderTemplate(hostPattern, context, parsedAdvancedConfig, warnings) : '';
        const urlPatternResolvedTemplate = urlPattern ? renderTemplate(urlPattern, context, parsedAdvancedConfig, warnings) : '';
        const resolvedUrlPattern = hostPatternResolvedTemplate + urlPatternResolvedTemplate;
        
        return {
          url: resolvedUrlPattern,
          warnings: warnings,
          hasMissingValues: warnings.length > 0
        };
    } catch (error) {
        console.error('Error resolving URL pattern:', error);
        return {
          url: '',
          warnings: [],
          hasMissingValues: false
        };
    }
}

/**
 * SECURITY NOTICE:
 * 
 * This module uses jexl for secure expression evaluation:
 * - Uses jexl library instead of Function constructor or eval()
 * - Built-in null-safety prevents errors on undefined/null property access
 * - Supports both simple property access and complex expressions uniformly
 * - Prevents code execution through controlled evaluation scope
 * - No known security vulnerabilities as of 2026
 * 
 * jexl is a secure JavaScript Expression Language that provides:
 * - Sandboxed evaluation environment
 * - No access to global objects or functions
 * - Safe property access with automatic null checking
 * 
 * When using resolveUrlPattern with user-controlled template strings,
 * jexl evaluates expressions safely within the provided context scope
 * and blocks malicious expressions instead of executing them.
 */

export { resolveUrlPattern, generateIncludeOnlyFromPattern, buildQueryString, buildAxiosParams };
export type { ResolutionResult, ResolutionWarning, IncludeOnlyResult };